// src/pages/Beschikbaarheid.js
// vBSH-006
// - Opmerking werkt nu als grote editor, gelijk aan Organisaties
// vBSH-005
// - Vanuit Planning kan een beschikbaarheidsregel direct worden heropend
// vBSH-003
// - Doorplannen in het weekend toegevoegd en gekoppeld aan Planning
// - Nieuw programma voor afwezigheid/beschikbaarheid medewerker
// - Basis voor Planning/Planbord kleuren: ziek, verlof, leegloop, opleiding, niet beschikbaar

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vBSH-007";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function safeTrim(s) {
  return String(s || "").trim();
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateNL(iso) {
  const s = isoDate(iso);
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return null;

  let cleaned = raw;
  if (cleaned.includes(",")) cleaned = cleaned.replace(/\./g, "").replace(",", ".");

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function decimalInput(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return true;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (["1", "true", "yes", "y", "j", "ja", "aan", "on"].includes(s)) return true;
  if (["0", "false", "no", "n", "nee", "uit", "off"].includes(s)) return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

function getSelectedCompanyCodeIdsHeader() {
  return (
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    sessionStorage.getItem("selectedCompanyCodeIds") ||
    localStorage.getItem("selectedCompanyCodeIds") ||
    ""
  );
}

async function tryFetchJson(urls, options = {}) {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
  const tenant = sessionStorage.getItem("tenant") || localStorage.getItem("tenant") || "demo";

  let parsedUser = {};
  try {
    parsedUser = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": parsedUser?.role || "user",
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": getSelectedCompanyCodeIdsHeader() || undefined,
      };

      Object.keys(headers).forEach((k) => headers[k] === undefined && delete headers[k]);

      const res = await fetch(buildUrl(url), { ...options, headers });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;
        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }
        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function employeeName(p = {}) {
  return safeTrim(p.search_name || [p.last_name, [p.first_name, p.name_prefix].filter(Boolean).join(" ")].filter(Boolean).join(", ") || p.name || "");
}

function companyCodeName(row = {}) {
  return safeTrim(row.company_code_name || row.company_code || row.bedrijfscode || "");
}

function normalizeRow(raw = {}) {
  return {
    ...raw,
    id: raw.id ?? null,
    personnel_id: raw.personnel_id ?? "",
    type: raw.type || "leegloop",
    start_date: isoDate(raw.start_date),
    end_date: isoDate(raw.end_date),
    hours_per_day: decimalInput(raw.hours_per_day, 2),
    plan_weekends: raw.plan_weekends === true,
    note: raw.note || "",
    active: parseActiveValue(raw) !== false,
  };
}

function emptyForm() {
  return {
    id: null,
    personnel_id: "",
    type: "leegloop",
    start_date: todayIso(),
    end_date: todayIso(),
    hours_per_day: "8,00",
    plan_weekends: false,
    note: "",
    active: true,
  };
}

const TYPE_OPTIONS = [
  ["leegloop", "Leegloop"],
  ["ziek", "Ziek"],
  ["verlof", "Verlof / vakantie"],
  ["opleiding", "Opleiding / cursus"],
  ["niet_beschikbaar", "Niet beschikbaar"],
  ["beschikbaar", "Beschikbaar"],
];

export default function Beschikbaarheid() {
  const rawUser = sessionStorage.getItem("user") || localStorage.getItem("user") || "";
  let role = "user";
  try { role = JSON.parse(rawUser || "{}")?.role || "user"; } catch {}
  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("search");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [flash, setFlash] = useState("");
  const [listIndex, setListIndex] = useState(-1);

  // Grote Opmerking-editor, gelijk aan Organisaties.
  const [remarkEditor, setRemarkEditor] = useState(false);

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const firstRef = useRef(null);
  const rowRefs = useRef([]);
  const rightScrollRef = useRef(null);
  const remarkSmallRef = useRef(null);
  const remarkBigRef = useRef(null);
  const ignoreNextRemarkSmallFocusRef = useRef(false);

  const isFormLocked = isReadonlyRole || mode !== "form";

  function openRemarkEditorAndFocusEnd() {
    if (isFormLocked) return;

    const panelTop = rightScrollRef.current?.scrollTop ?? 0;

    setForm((prev) => {
      const cur = String(prev.note || "");
      if (!cur || cur.endsWith("\n")) return prev;
      return { ...prev, note: cur + "\n" };
    });

    setRemarkEditor(true);

    requestAnimationFrame(() => {
      try {
        if (rightScrollRef.current) rightScrollRef.current.scrollTop = panelTop;
      } catch {}

      requestAnimationFrame(() => {
        try {
          const el = remarkBigRef.current;
          if (!el) return;
          try {
            el.focus?.({ preventScroll: true });
          } catch {
            el.focus?.();
          }
          const len = String(el.value || "").length;
          el.setSelectionRange?.(len, len);
        } catch {}
      });
    });
  }

  function closeRemarkEditor() {
    const panel = rightScrollRef.current;
    const panelTop = panel?.scrollTop ?? 0;

    setRemarkEditor(false);

    requestAnimationFrame(() => {
      try {
        if (panel) panel.scrollTop = panelTop;
      } catch {}

      requestAnimationFrame(() => {
        try {
          const el = remarkSmallRef.current;
          if (!el) return;

          ignoreNextRemarkSmallFocusRef.current = true;

          try {
            el.focus?.({ preventScroll: true });
          } catch {
            el.focus?.();
          }

          if (panel && window.innerWidth > 768) {
            const panelRect = panel.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const currentTop = panel.scrollTop;
            const relativeBottom = elRect.bottom - panelRect.top + currentTop;

            panel.scrollTop = Math.max(
              0,
              relativeBottom - panel.clientHeight + 10
            );
          } else {
            el.scrollIntoView?.({
              behavior: "auto",
              block: "end",
              inline: "nearest",
            });
          }
        } catch {}
      });
    });
  }

  async function loadPersonnel() {
    try {
      const data = await tryFetchJson(["/api/personnel", "/api/personeel"]);
      setPersonnel(Array.isArray(data) ? data : []);
    } catch {
      setPersonnel([]);
    }
  }

  async function loadList(keepSelection = true) {
    setLoading(true);
    setError("");
    try {
      const data = await tryFetchJson(["/api/personnel-availability", "/api/beschikbaarheid"]);
      const rows = Array.isArray(data) ? data.map(normalizeRow) : [];
      setItems(rows);

      if (keepSelection && selectedId) {
        const idx = rows.findIndex((x) => String(x.id) === String(selectedId));
        if (idx >= 0) setListIndex(idx);
      } else {
        setListIndex(rows.length ? 0 : -1);
      }

      return rows;
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
      setListIndex(-1);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPersonnel();

    (async () => {
      const rows = await loadList(false);

      let reopenId = "";
      try {
        reopenId = sessionStorage.getItem("staffiq_reopen_availability_id") || "";
        sessionStorage.removeItem("staffiq_reopen_availability_id");
      } catch {}

      if (!reopenId || !Array.isArray(rows)) return;

      const row = rows.find((x) => String(x.id) === String(reopenId));
      if (!row) return;

      setSelectedId(row.id);
      setForm(normalizeRow(row));
      setMode("form");
      setSaveError("");
      setTimeout(() => rightScrollRef.current?.scrollTo?.({ top: 0 }), 0);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getPersonName(id) {
    const p = (personnel || []).find((x) => String(x.id) === String(id));
    return employeeName(p) || "";
  }

  const filtered = useMemo(() => {
    const query = norm(q);
    return (items || []).filter((it) => {
      const text = [it.personnel_name || getPersonName(it.personnel_id), it.type, it.start_date, it.end_date, it.note, companyCodeName(it)].join(" ");
      return !query || norm(text).includes(query);
    });
  }, [items, q, personnel]);

  const sorted = useMemo(() => {
    return filtered.slice().sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")) || String(a.personnel_name || "").localeCompare(String(b.personnel_name || ""), "nl"));
  }, [filtered]);

  function selectRow(row, idx) {
    setSelectedId(row.id);
    setForm(normalizeRow(row));
    setListIndex(idx);
    setMode("form");
    setRemarkEditor(false);
    setSaveError("");
    setTimeout(() => rightScrollRef.current?.scrollTo?.({ top: 0 }), 0);
  }

  function newItem() {
    if (isReadonlyRole) return;
    setSelectedId(null);
    setForm(emptyForm());
    setMode("form");
    setRemarkEditor(false);
    setSaveError("");
    setListIndex(-1);
    setTimeout(() => firstRef.current?.focus?.(), 80);
  }

  function resetAfterSave() {
    setSelectedId(null);
    setForm({ ...emptyForm(), start_date: "", end_date: "" });
    setRemarkEditor(false);
    setMode("search");
    setSaveError("");
    setListIndex(items.length ? 0 : -1);

    const resetScroll = () => {
      try {
        if (rightScrollRef.current) {
          rightScrollRef.current.scrollTop = 0;
          rightScrollRef.current.scrollLeft = 0;
        }
      } catch {}

      try {
        const topEl = pageTopRef.current || searchRef.current;
        topEl?.scrollIntoView?.({
          behavior: "auto",
          block: "start",
          inline: "nearest",
        });
      } catch {}

      try {
        window.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch {}

      if (window.innerWidth > 768) {
        try {
          searchRef.current?.focus?.({ preventScroll: true });
          searchRef.current?.select?.();
        } catch {
          try {
            searchRef.current?.focus?.();
            searchRef.current?.select?.();
          } catch {}
        }
      }
    };

    resetScroll();
    requestAnimationFrame(() => {
      resetScroll();
      requestAnimationFrame(resetScroll);
    });
    [50, 120, 250, 500].forEach((ms) => setTimeout(resetScroll, ms));
  }

  async function saveItem() {
    if (isReadonlyRole) return;
    setSaveError("");

    if (!form.personnel_id) {
      setSaveError("Kies eerst een medewerker.");
      return;
    }

    const payload = {
      ...form,
      personnel_id: Number(form.personnel_id),
      hours_per_day: parseDecimal(form.hours_per_day),
      plan_weekends: form.plan_weekends === true,
      active: form.active !== false,
    };

    try {
      const saved = await tryFetchJson(
        form.id ? [`/api/personnel-availability/${form.id}`, `/api/beschikbaarheid/${form.id}`] : ["/api/personnel-availability", "/api/beschikbaarheid"],
        {
          method: form.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      normalizeRow(saved); // response bewust wel valideren, maar niet opnieuw in rechterkolom tonen
      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 1200);

      await loadList(false);

      // Na opslaan: rechterkolom leegmaken en programma terug naar boven.
      resetAfterSave();
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonlyRole || !form.id) return;
    if (!window.confirm("Beschikbaarheidsregel verwijderen?")) return;

    try {
      await tryFetchJson([`/api/personnel-availability/${form.id}`, `/api/beschikbaarheid/${form.id}`], { method: "DELETE" });
      setSelectedId(null);
      setForm(emptyForm());
      setMode("search");
      await loadList(false);
      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 1200);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function refreshList() {
    setSelectedId(null);
    setForm({ ...emptyForm(), start_date: "", end_date: "" });
    setRemarkEditor(false);
    setMode("search");
    setSaveError("");
    await loadList(false);
    setTimeout(() => searchRef.current?.focus?.(), 80);
  }

  function moveCursor(delta) {
    if (!sorted.length) return;
    const next = Math.max(0, Math.min(sorted.length - 1, (listIndex < 0 ? 0 : listIndex) + delta));
    setListIndex(next);
    setTimeout(() => rowRefs.current[next]?.scrollIntoView?.({ block: "nearest" }), 0);
  }

  function openCurrentRow() {
    const row = sorted[listIndex < 0 ? 0 : listIndex];
    if (row) selectRow(row, listIndex < 0 ? 0 : listIndex);
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (cmd?.type === "new") return newItem(), true;
      if (cmd?.type === "refresh") return refreshList(), true;
      if (cmd?.type === "save") return saveItem(), true;
      if (cmd?.type === "delete") return deleteItem(), true;
      if (cmd?.type === "select") return openCurrentRow(), true;
      if (cmd?.type === "next") return moveCursor(+1), true;
      if (cmd?.type === "prev") return moveCursor(-1), true;
      return false;
    },
    onDictation: (text) => {
      const t = safeTrim(text);
      if (!t) return;
      if (norm(t) === "nieuw") return newItem();
      if (norm(t) === "vernieuw") return refreshList();
      if (norm(t) === "opslaan") return saveItem();
      setQ(t);
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;

  const buttonStyle = { padding: "7px 12px", border: "1px solid #d9d9d9", borderRadius: 4, background: "white", cursor: "pointer", whiteSpace: "nowrap" };
  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };
  const inputStyle = { display: "block", padding: "7px 10px", width: "100%", border: "1px solid #d9d9d9", borderRadius: 4, background: "white", boxSizing: "border-box" };
  const selectStyle = { ...inputStyle, height: 32, padding: "0 8px" };
  const labelStyle = { fontSize: 13, color: "#333" };
  const tdStyle = { padding: "7px 6px", borderBottom: "1px solid #efefef", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  const thStyle = { ...tdStyle, fontWeight: 700, position: "sticky", top: 0, background: "#fff", zIndex: 2 };

  function typeLabel(value) {
    return TYPE_OPTIONS.find(([v]) => v === value)?.[1] || value;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `minmax(0,1fr) ${RIGHT_WIDTH}px`, gap: isMobile ? 18 : 14, alignItems: "start" }}>
      <div style={{ minWidth: 0 }}>
        <div
          ref={pageTopRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 0,
            marginBottom: 10,
          }}
        >
          <h1 style={{ margin: 0 }}>
            Beschikbaarheid <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

          <HelpButton
            topic="beschikbaarheid"
            title="Help over Beschikbaarheid"
          />
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek..." style={{ ...inputStyle, width: isMobile ? "100%" : 220 }} />
          <div style={{ minWidth: 52, height: 32, border: "1px solid #d9d9d9", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#1f4e79", background: "#f7fafc" }}>{sorted.length}</div>
          <button onClick={() => voice.startOnce?.()} disabled={!voice.supported || voice.listening} style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}>{voice.listening ? "🎤..." : "🎤 Spreek"}</button>
          <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>➕ Nieuw</button>
          <button onClick={refreshList} style={buttonStyle}>🔄 Vernieuw</button>
        </div>

        {flash && <div style={{ marginTop: 8, color: "#1f7a1f" }}>{flash}</div>}
        {error && <div style={{ marginTop: 8, color: "#b00020" }}>{error}</div>}

        <div style={{ marginTop: 12, border: "1px solid #e5e5e5", borderRadius: 6, background: "white", overflow: "auto", maxHeight: isMobile ? "45vh" : "calc(100dvh - 135px)" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 820, tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={thStyle}>Medewerker</th>
                <th style={{ ...thStyle, width: 120 }}>Type</th>
                <th style={{ ...thStyle, width: 90 }}>Van</th>
                <th style={{ ...thStyle, width: 90 }}>Tot</th>
                <th style={{ ...thStyle, width: 70 }}>Uren</th>
                <th style={{ ...thStyle, width: 120 }}>Bedrijfscode</th>
                <th style={{ ...thStyle, width: 60 }}>Actief</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={tdStyle}>Laden...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7} style={tdStyle}>Geen beschikbaarheidsregels gevonden.</td></tr>
              ) : sorted.map((it, idx) => (
                <tr key={it.id || idx} ref={(el) => (rowRefs.current[idx] = el)} onClick={() => selectRow(it, idx)} style={{ cursor: "pointer", background: String(it.id) === String(selectedId) ? "#e6f0ff" : idx === listIndex ? "#f3f7ff" : "white" }}>
                  <td style={tdStyle}>{it.personnel_name || getPersonName(it.personnel_id)}</td>
                  <td style={tdStyle}>{typeLabel(it.type)}</td>
                  <td style={tdStyle}>{formatDateNL(it.start_date)}</td>
                  <td style={tdStyle}>{formatDateNL(it.end_date)}</td>
                  <td style={tdStyle}>{it.hours_per_day}</td>
                  <td style={tdStyle}>{companyCodeName(it)}</td>
                  <td style={tdStyle}>{boolToJaNee(it.active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div ref={rightScrollRef} style={{ width: RIGHT_WIDTH, minWidth: isMobile ? 0 : RIGHT_WIDTH, position: isMobile ? "relative" : "sticky", top: 0, maxHeight: isMobile ? "none" : "calc(100vh - 40px)", overflowY: isMobile ? "visible" : "auto", background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)", borderRadius: 10, padding: 10, boxSizing: "border-box" }}>
        <div style={{ background: "white", borderRadius: 10, padding: 12 }}>
          <h2
            style={{
              marginTop: 0,
              fontSize: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>{remarkEditor ? "Opmerking" : "Beschikbaarheid"}</span>

            {remarkEditor && (
              <button
                type="button"
                onClick={closeRemarkEditor}
                style={buttonStyle}
                title="Terug naar normaal formulier"
              >
                ↩ Terug
              </button>
            )}
          </h2>

          {saveError && <div style={{ marginBottom: 10, color: "#b00020", fontSize: 13 }}>{saveError}</div>}

          {remarkEditor ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                Opmerking-editor (spreek: door-dicteren) — zeg <b>punt</b> voor “.”.
              </div>

              <textarea
                ref={remarkBigRef}
                value={form.note || ""}
                disabled={isFormLocked}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={15}
                style={{
                  padding: "10px 10px",
                  width: "100%",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  background: "white",
                  resize: "vertical",
                  boxSizing: "border-box",
                  minHeight: "60vh",
                  fontSize: 14,
                  lineHeight: 1.4,
                }}
              />

              <div
                style={{
                  background: "white",
                  paddingTop: 10,
                  marginTop: 6,
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  borderTop: "1px solid #eee",
                }}
              >
                <button
                  onClick={saveItem}
                  disabled={isFormLocked}
                  style={
                    isFormLocked
                      ? { ...buttonStyle, background: "#2f80d0", color: "white", opacity: 0.55, cursor: "not-allowed" }
                      : { ...buttonStyle, background: "#2f80d0", color: "white" }
                  }
                >
                  💾 Opslaan
                </button>

                <button
                  onClick={deleteItem}
                  disabled={isFormLocked || !form.id}
                  style={
                    isFormLocked || !form.id
                      ? { ...buttonStyle, background: "#d33", color: "white", opacity: 0.55, cursor: "not-allowed" }
                      : { ...buttonStyle, background: "#d33", color: "white" }
                  }
                >
                  🗑 Verwijderen
                </button>
              </div>
            </div>
          ) : (
          <div style={{ display: "grid", gap: 8 }}>
            <label style={labelStyle}>Medewerker</label>
            <select ref={firstRef} value={form.personnel_id || ""} disabled={isFormLocked} onChange={(e) => setForm({ ...form, personnel_id: e.target.value })} style={selectStyle}>
              <option value="">-</option>
              {(personnel || []).map((p) => <option key={p.id} value={p.id}>{employeeName(p)}</option>)}
            </select>

            <label style={labelStyle}>Type</label>
            <select value={form.type || ""} disabled={isFormLocked} onChange={(e) => setForm({ ...form, type: e.target.value })} style={selectStyle}>
              {TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>

            <label style={labelStyle}>Van datum</label>
            <input type="date" value={form.start_date || ""} disabled={isFormLocked} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })} style={inputStyle} />

            <label style={labelStyle}>Tot datum</label>
            <input type="date" value={form.end_date || ""} disabled={isFormLocked} onChange={(e) => setForm({ ...form, end_date: e.target.value })} style={inputStyle} />

            <label style={labelStyle}>Uren per dag</label>
            <input value={form.hours_per_day || ""} disabled={isFormLocked} onChange={(e) => setForm({ ...form, hours_per_day: e.target.value.replace(/[^0-9,.-]/g, "") })} onBlur={() => setForm({ ...form, hours_per_day: decimalInput(form.hours_per_day, 2) })} style={inputStyle} placeholder="8,00" />

            <label style={labelStyle}>Doorplannen in het weekend</label>
            <select value={form.plan_weekends === true ? "j" : "n"} disabled={isFormLocked} onChange={(e) => setForm({ ...form, plan_weekends: e.target.value === "j" })} style={selectStyle}>
              <option value="n">Nee</option>
              <option value="j">Ja</option>
            </select>

            <label style={labelStyle}>Opmerking</label>
            <textarea
              ref={remarkSmallRef}
              value={form.note || ""}
              disabled={isFormLocked}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              onFocus={() => {
                setMode("form");

                if (ignoreNextRemarkSmallFocusRef.current) {
                  ignoreNextRemarkSmallFocusRef.current = false;
                  return;
                }

                openRemarkEditorAndFocusEnd();
              }}
              style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
            />

            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
              <input type="checkbox" checked={form.active === true} disabled={isFormLocked} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Actief
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <button onClick={saveItem} disabled={isFormLocked} style={isFormLocked ? buttonDisabledStyle : { ...buttonStyle, background: "#2f80d0", color: "white" }}>💾 Opslaan</button>
              <button onClick={deleteItem} disabled={isFormLocked || !form.id} style={isFormLocked || !form.id ? buttonDisabledStyle : { ...buttonStyle, background: "#d33", color: "white" }}>🗑 Verwijderen</button>
            </div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Regel-id: {form.id || "nieuw"}</div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
